<script setup>
import { computed } from 'vue';

const props = defineProps({
  profile: { type: Object, default: () => ({}) },
  industry: { type: Object, default: null },
  industryHints: { type: Object, default: () => ({}) },
});

const UI_LABELS = {
  title: 'Svar som Chatlotta kan förhandsvisa',
  missing: 'Saknar godkänd data för förhandsvisning.',
  openingHours: 'Öppettider',
  contact: 'Adress och kontakt',
  services: 'Tjänster och priser',
  booking: 'Bokning',
  fallback: 'Fallback',
};

const hasValue = value => String(value || '').trim().length > 0;

const bookingAnswer = computed(() => {
  if (
    hasValue(props.profile.booking?.available_days) &&
    hasValue(props.profile.booking?.available_times)
  ) {
    const subject = props.industry?.label_sv
      ? `för ${props.industry.label_sv.toLowerCase()}`
      : 'för bokning';
    return `Det finns tider ${subject} ${props.profile.booking.available_days}: ${props.profile.booking.available_times}.`;
  }

  if (hasValue(props.profile.opening_hours) && hasValue(props.profile.phone)) {
    return `För bokning kan du kontakta oss på ${props.profile.phone}. Vi utgår från våra öppettider: ${props.profile.opening_hours}.`;
  }

  return '';
});

const servicesLabel = computed(
  () => props.industryHints.services_label_sv || UI_LABELS.services
);

const previewItems = computed(() => [
  {
    id: 'opening_hours',
    label: UI_LABELS.openingHours,
    answer: hasValue(props.profile.opening_hours)
      ? `Vi har öppet ${props.profile.opening_hours}.`
      : '',
  },
  {
    id: 'contact',
    label: UI_LABELS.contact,
    answer: [
      hasValue(props.profile.address)
        ? `Du hittar oss på ${props.profile.address}.`
        : '',
      hasValue(props.profile.phone)
        ? `Du kan ringa oss på ${props.profile.phone}.`
        : '',
      hasValue(props.profile.email)
        ? `Du kan mejla oss på ${props.profile.email}.`
        : '',
    ]
      .filter(Boolean)
      .join(' '),
  },
  {
    id: 'services',
    label: servicesLabel.value,
    answer: [
      hasValue(props.profile.services_products)
        ? `Vi erbjuder ${props.profile.services_products}.`
        : '',
      hasValue(props.profile.prices)
        ? `Våra priser är ${props.profile.prices}.`
        : '',
    ]
      .filter(Boolean)
      .join(' '),
  },
  {
    id: 'booking',
    label: UI_LABELS.booking,
    answer: bookingAnswer.value,
  },
  {
    id: 'fallback',
    label: UI_LABELS.fallback,
    answer: hasValue(props.profile.fallback_message)
      ? props.profile.fallback_message
      : 'Jag är inte helt säker på det, men jag kan skicka vidare din fråga till teamet.',
  },
]);
</script>

<template>
  <section class="grid gap-3 rounded-md border border-n-weak p-4">
    <h3 class="mb-0 text-sm font-medium text-n-slate-12">
      {{ UI_LABELS.title }}
    </h3>
    <div class="grid gap-2">
      <article
        v-for="item in previewItems"
        :key="item.id"
        class="rounded-md bg-n-alpha-2 px-3 py-2"
      >
        <div class="text-xs font-medium uppercase text-n-slate-10">
          {{ item.label }}
        </div>
        <p class="mb-0 mt-1 text-sm text-n-slate-12">
          {{ item.answer || UI_LABELS.missing }}
        </p>
      </article>
    </div>
  </section>
</template>
